import type { Strain, StrainInput, StrainSummary } from '@shared';
import {
  getLocalCatalogueImport,
  type StrainWithLocalImport,
} from '../catalogueBootstrap/provenance';

function orNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function numOrNull(value: number | null | undefined): number | null {
  return value === undefined || value === null ? null : value;
}

function withPreservedLocalImport(
  strain: Strain,
  existing?: Strain,
): Strain & StrainWithLocalImport {
  const provenance = existing
    ? getLocalCatalogueImport(existing as StrainWithLocalImport)
    : getLocalCatalogueImport(strain as StrainWithLocalImport);
  if (!provenance) return strain;
  return { ...strain, localCatalogueImport: provenance };
}

/**
 * Build a full Strain record from validated input, preserving identity and
 * server-owned fields from an existing row when updating.
 */
export function buildStrainFromInput(
  id: string,
  input: StrainInput,
  existing?: Strain,
): Strain {
  const now = new Date().toISOString();
  // Preserve bootstrap provenance / imported sourceType; do not reclassify
  // imported local rows as spreadsheet merely because productId was copied.
  const existingImport = existing
    ? getLocalCatalogueImport(existing as StrainWithLocalImport)
    : null;
  let sourceType: Strain['sourceType'];
  if (existingImport || existing?.sourceType === 'imported') {
    sourceType = 'imported';
  } else if (existing?.sourceType === 'spreadsheet' || existing?.productId) {
    sourceType = 'spreadsheet';
  } else if (input.sourceType && input.sourceType !== 'spreadsheet') {
    sourceType = input.sourceType;
  } else {
    sourceType = 'local_manual';
  }

  const built: Strain = {
    id,
    productId: existing?.productId ?? null,
    name: orNull(input.name ?? null),
    productName: orNull(input.productName ?? null),
    producer: orNull(input.producer ?? null),
    brand: orNull(input.brand ?? null),
    category: orNull(input.category ?? null),
    cultivarType: orNull(input.cultivarType ?? null),
    lineageSourceText: orNull(input.lineageSourceText ?? null),
    strainClassSourceText: orNull(input.strainClassSourceText ?? null),
    thcPercent: input.thcPercent,
    cbdPercent: numOrNull(input.cbdPercent),
    cbnPercent: numOrNull(input.cbnPercent),
    cbgPercent: numOrNull(input.cbgPercent),
    thcvPercent: numOrNull(input.thcvPercent),
    cbcPercent: numOrNull(input.cbcPercent),
    totalTerpenesPercent: numOrNull(input.totalTerpenesPercent),
    packageDate: orNull(input.packageDate ?? null),
    purchaseDate: orNull(input.purchaseDate ?? null),
    lotNumber: orNull(input.lotNumber ?? null),
    sourceType,
    notes: orNull(input.notes ?? null),
    referenceNotes: orNull(input.referenceNotes ?? null),
    isVerified: input.isVerified ?? false,
    isArchived: existing?.isArchived ?? false,
    isBalancingStrain: existing?.isBalancingStrain ?? false,
    saveStatus: existing?.saveStatus ?? 'complete',
    lastSyncedAt: existing?.lastSyncedAt ?? null,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    images: existing?.images ? [...existing.images] : [],
    primaryImage: existing?.primaryImage ?? null,
  };
  return withPreservedLocalImport(built, existing);
}

function imageDisplayUrl(image: {
  url: string;
  thumbnailUrl: string | null;
}): string {
  return image.thumbnailUrl ?? image.url;
}

/** Convert a full Strain into list-card summary fields. */
export function toStrainSummary(strain: Strain): StrainSummary {
  const localImages = strain.images.filter(
    (img) => (img.source ?? 'local') !== 'spreadsheet',
  );
  const spreadsheetImages = strain.images.filter(
    (img) => img.source === 'spreadsheet',
  );
  const localPrimaryOnly = localImages.find((i) => i.isPrimary) ?? null;
  const firstLocal = localImages[0] ?? null;
  const spreadsheetThumbnailUrl = spreadsheetImages[0]
    ? imageDisplayUrl(spreadsheetImages[0])
    : null;
  const localPrimaryThumbnailUrl = localPrimaryOnly
    ? imageDisplayUrl(localPrimaryOnly)
    : null;
  const firstLocalThumbnailUrl = firstLocal
    ? imageDisplayUrl(firstLocal)
    : null;
  const primaryThumbnailUrl =
    spreadsheetThumbnailUrl ??
    localPrimaryThumbnailUrl ??
    firstLocalThumbnailUrl;

  const { images: _images, primaryImage: _primary, ...rest } = strain;
  return {
    ...rest,
    imageCount: strain.images.length,
    primaryThumbnailUrl,
    spreadsheetThumbnailUrl,
    localPrimaryThumbnailUrl,
    firstLocalThumbnailUrl,
  };
}

/** Map a Strain into StrainInput fields for duplicate. */
export function strainToDuplicateInput(existing: Strain): StrainInput {
  return {
    name: existing.name ? `${existing.name} (copy)` : undefined,
    productName: existing.productName ?? undefined,
    producer: existing.producer ?? undefined,
    brand: existing.brand ?? undefined,
    category: existing.category ?? undefined,
    cultivarType: existing.cultivarType ?? undefined,
    strainClassSourceText: existing.strainClassSourceText ?? undefined,
    lineageSourceText: existing.lineageSourceText ?? undefined,
    thcPercent: existing.thcPercent,
    cbdPercent: existing.cbdPercent ?? undefined,
    cbnPercent: existing.cbnPercent ?? undefined,
    cbgPercent: existing.cbgPercent ?? undefined,
    thcvPercent: existing.thcvPercent ?? undefined,
    cbcPercent: existing.cbcPercent ?? undefined,
    totalTerpenesPercent: existing.totalTerpenesPercent ?? undefined,
    packageDate: existing.packageDate ?? undefined,
    purchaseDate: existing.purchaseDate ?? undefined,
    lotNumber: existing.lotNumber ?? undefined,
    sourceType: 'local_manual',
    notes: existing.notes ?? undefined,
    referenceNotes: existing.referenceNotes ?? undefined,
    isVerified: false,
  };
}
